import { atom, type Atom, type WritableAtom } from "jotai";
import { jotaiStore } from "@/components/providers/jotai-provider";
import { withAtomEffect } from "jotai-effect";
import { ConvexClient } from "convex/browser";
import { convexClient } from "@/app/ConvexClientProvider";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import {
  PARTICIPANT_PROPERTY_FIELDS,
  type ParticipantPropertyName,
} from "@convex/shared";

// Separate ConvexClient for subscriptions (ConvexReactClient doesn't have onUpdate)
// Auth is set via setSubscriptionClientAuth() when ConvexProviderWithClerk mounts
const convexUrl = typeof window !== "undefined" ? process.env.NEXT_PUBLIC_CONVEX_URL : undefined;
let subscriptionClient: ConvexClient | null = null;

export function getSubscriptionClient() {
  if (!subscriptionClient && convexUrl) {
    subscriptionClient = new ConvexClient(convexUrl);
  }
  return subscriptionClient;
}

// Called by ConvexClientProvider to set auth on the subscription client
export function initSubscriptionClientAuth(fetchToken: () => Promise<string | null>) {
  const client = getSubscriptionClient();
  if (client) {
    client.setAuth(fetchToken);
  }
}

export type ParticipantPropertyAtoms<T> = readonly [
  WritableAtom<T | undefined, [T | undefined], void>,
  Atom<Record<string, T | undefined>>,
  Atom<null>, // Stubbed out - will be replaced with Convex query for previous session
];

type Options = {
  debounce?: boolean | number;
};

type ParticipantContext = {
  sessionId: Id<"sessions">;
  participantKey: string;
  userId?: string | null; // Clerk user ID for logged-in users
  anonUserId?: string | null; // localStorage UUID for anonymous users
};

const participantContextAtom = atom<ParticipantContext | null>(null);

const registry = new Map<string, RegistryEntry<any>>();

/**
 * Get the key to use for looking up this participant's data.
 * Priority: Clerk ID > Anonymous UUID > Jitsi ID
 */
function getLookupKey(ctx: ParticipantContext): string {
  // Clerk user ID (starts with "user_")
  if (ctx.userId && ctx.userId.startsWith("user_")) {
    return ctx.userId;
  }
  // Anonymous user ID from localStorage (starts with "anon_")
  if (ctx.anonUserId && ctx.anonUserId.startsWith("anon_")) {
    return ctx.anonUserId; // Stable across refreshes!
  }
  // Fallback to Jitsi ID (changes on refresh)
  return ctx.participantKey;
}

/**
 * Get the shared ConvexReactClient singleton.
 * This client is authenticated via ConvexProviderWithClerk once React mounts.
 */
function getConvexClient() {
  if (!convexClient) {
    throw new Error("Convex client not initialized - NEXT_PUBLIC_CONVEX_URL may not be set");
  }
  return convexClient;
}

function propertyNameFromInput(name: string): ParticipantPropertyName {
  if (name in PARTICIPANT_PROPERTY_FIELDS) {
    return name as ParticipantPropertyName;
  }
  throw new Error(`Unsupported property ${name}`);
}

function valuesEqual(a: unknown, b: unknown) {
  if (a === b) return true;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

type RegistryEntry<T> = {
  name: ParticipantPropertyName;
  baseLocalAtom: WritableAtom<T | undefined, [T | undefined], void>;
  localAtom: WritableAtom<T | undefined, [T | undefined], void>;
  allAtom: Atom<Record<string, T | undefined>>;
  remoteStateBaseAtom: WritableAtom<
    Record<string, T | undefined>,
    [
      | Record<string, T | undefined>
      | ((prev: Record<string, T | undefined>) => Record<string, T | undefined>),
    ],
    void
  >;
  previousSessionAtom: Atom<null>;
};

async function syncToConvex<T>(
  ctx: ParticipantContext | null,
  name: ParticipantPropertyName,
  value: T | undefined,
  onComplete?: () => void,
) {
  if (!ctx) {
    onComplete?.();
    return;
  }
  try {
    await getConvexClient().mutation(api.sessions.setParticipantProperty, {
      sessionId: ctx.sessionId,
      property: name,
      value: value ?? null,
      participantKey: ctx.participantKey,
      anonUserId: ctx.anonUserId ?? undefined,
    });
  } catch (error: unknown) {
    console.error(`[convex-properties] failed to sync ${name}`, error);
  } finally {
    onComplete?.();
  }
}

export function attachParticipantPropertyConvex(params: ParticipantContext) {
  if (typeof window === "undefined") return;
  if (!convexClient) {
    console.warn("Convex client not initialized; participant properties disabled.");
    return;
  }
  jotaiStore.set(participantContextAtom, params);
  getConvexClient()
    .mutation(api.sessions.ensureParticipant, {
      sessionId: params.sessionId,
      participantKey: params.participantKey,
      anonUserId: params.anonUserId ?? undefined,
    })
    .catch((error: unknown) => {
      console.error("Failed to ensure participant", error);
    });
}

export function detachParticipantPropertyConvex() {
  if (typeof window === "undefined" || !convexClient) return;
  const previous = jotaiStore.get(participantContextAtom);
  jotaiStore.set(participantContextAtom, null);
  if (previous?.participantKey) {
    getConvexClient()
      .mutation(api.sessions.clearParticipantKey, {
        sessionId: previous.sessionId,
        participantKey: previous.participantKey,
      })
      .catch((error: unknown) => {
        console.error("Failed to clear participant key", error);
      });
  }
}

export function createParticipantPropertyAtom<T>(
  propertyName: ParticipantPropertyName,
  options?: Options,
): ParticipantPropertyAtoms<T> {
  const name = propertyNameFromInput(propertyName);
  const existing = registry.get(name);
  if (existing) {
    return [existing.localAtom, existing.allAtom, existing.previousSessionAtom];
  }

  // Simple in-memory atom for optimistic updates
  const baseLocalAtom: WritableAtom<T | undefined, [T | undefined], void> = atom<T | undefined>(
    undefined,
  );
  // Stubbed out - will be replaced with Convex query for previous session data
  const previousSessionAtom: Atom<null> = atom(() => null);

  // Track pending local updates - don't let subscription overwrite while pending.
  // Use an id (not boolean) to avoid races with multiple in-flight syncs.
  let updateCounter = 0;
  let pendingUpdateId = 0;

  const debounceMs = options?.debounce
    ? typeof options.debounce === "number"
      ? options.debounce
      : 300
    : 0;

  const remoteStateBaseAtom = atom<Record<string, T | undefined>>({});

  let debounceTimeout: ReturnType<typeof setTimeout> | null = null;
  let lastContextSignature: string | null = null;

  const remoteStateAtom = withAtomEffect(remoteStateBaseAtom, (get, set) => {
    if (typeof window === "undefined" || !convexClient) {
      return;
    }

    const ctx = get(participantContextAtom);
    const contextSignature = ctx ? `${ctx.sessionId}:${getLookupKey(ctx)}` : null;
    if (contextSignature !== lastContextSignature) {
      lastContextSignature = contextSignature;
      pendingUpdateId = 0;
      set(remoteStateBaseAtom, {});
      set(baseLocalAtom, undefined);
    }

    if (!ctx) return;

    let unsub: (() => void) | undefined;
    try {
      const client = getSubscriptionClient();
      if (!client) {
        return;
      }
      unsub = client.onUpdate(
        api.sessions.participantPropertyMap,
        { sessionId: ctx.sessionId, property: name },
        (record) => {
          const activeCtx = get(participantContextAtom);
          if (!activeCtx || activeCtx.sessionId !== ctx.sessionId) {
            return;
          }
          const data = (record ?? {}) as Record<string, T | undefined>;
          set(remoteStateBaseAtom, data);
          const lookupKey = getLookupKey(activeCtx);
          const localValue = data[lookupKey];
          const currentValue = get(baseLocalAtom);
          if (pendingUpdateId !== 0) {
            return;
          }
          if (localValue !== undefined && !valuesEqual(localValue, currentValue)) {
            set(baseLocalAtom, localValue);
          }
        },
        (error: unknown) => {
          console.error(`[convex-properties] subscription error for ${name}`, error);
        },
      );
    } catch (error: unknown) {
      console.error("[convex-properties] Failed to subscribe to participant property", error);
    }

    return () => {
      unsub?.();
    };
  });

  const allAtom = atom((get) => get(remoteStateAtom));

  const localAtom: WritableAtom<T | undefined, [T | undefined], void> = atom(
    (get) => {
      // Ensure the subscription effect is active whenever this property is used.
      get(remoteStateAtom);
      return get(baseLocalAtom);
    },
    (get, set, update: T | undefined | ((prev: T | undefined) => T | undefined)) => {
      const prev = get(baseLocalAtom);
      const next =
        typeof update === "function"
          ? (update as (value: T | undefined) => T | undefined)(prev)
          : update;
      set(baseLocalAtom, next);
      const updateId = ++updateCounter;
      pendingUpdateId = updateId;

      const ctxSnapshot = get(participantContextAtom);
      const lookupKey = ctxSnapshot ? getLookupKey(ctxSnapshot) : null;
      if (ctxSnapshot && lookupKey) {
        set(remoteStateBaseAtom, (previous) => {
          const merged = { ...previous };
          if (next === undefined || next === null) {
            delete merged[lookupKey];
          } else {
            merged[lookupKey] = next;
          }
          return merged;
        });
      }

      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
        debounceTimeout = null;
      }

      const scheduleSync = () => {
        void syncToConvex(ctxSnapshot, name, next, () => {
          if (pendingUpdateId === updateId) {
            pendingUpdateId = 0;
          }
        });
      };

      if (debounceMs > 0) {
        debounceTimeout = setTimeout(scheduleSync, debounceMs);
      } else {
        scheduleSync();
      }
    },
  );

  const entry: RegistryEntry<T> = {
    name,
    baseLocalAtom,
    localAtom,
    allAtom,
    remoteStateBaseAtom,
    previousSessionAtom,
  };

  registry.set(name, entry);

  return [entry.localAtom, entry.allAtom, entry.previousSessionAtom];
}
